import * as React from "react";
import Keycloak from 'keycloak-js';
import Api from "../../api";
import {
  Event,
  SowingEventData,
  WastageEventData,
  CultivationAction,
  SeedBatch,
  WastageReason,
  EventType,
  Facility,
  Location,
  TransferEventData,
  Batch,
  PackingEventData,
  PerformedActionEventData,
  LoadEventData,
  PeatBatch,
  GritBatch,
  BatchStatus,
  PackingContainerType,
  PackingType,
  TreeSpecies} from "../../generated/client";
import { Navigate } from 'react-router-dom';
import strings from "../../localization/strings";
import { DateTimeInput } from 'semantic-ui-calendar-react';
import * as actions from "../../actions";
import { StoreState } from "../../types/index";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { DateTime } from "luxon";

import {
  Grid,
  Button,
  Loader,
  Form,
  Message,
  DropdownProps,
  Confirm,
  TextAreaProps,
  DropdownItemProps,
  InputOnChangeData,
  Select,
  Checkbox
} from "semantic-ui-react";
import LocalizedUtils from "../../localization/localizedutils";
import moment from "moment";
import { ErrorMessage } from "../../types";
import { FormContainer } from "../FormContainer";

/**
 * Interface representing component properties
 */
interface Props {
  keycloak?: Keycloak;
  facility: Facility;
  onError: (error: ErrorMessage | undefined) => void;
}

/**
 * Interface representing component state
 */
interface State {
  open: boolean;
  loading: boolean;
  saving: boolean;
  messageVisible: boolean;
  redirect: boolean;
  event: Event;
  generatedWoodType: string;
  performedCultivationActions?: CultivationAction[];
  seedBatches?: SeedBatch[];
  locations: Location[];
  wastageReasons?: WastageReason[];
  batches: Batch[];
  peatBatches: PeatBatch[];
  gritBatches: GritBatch[];
  treeSpecies: TreeSpecies[];
  durationHours: number;
  durationMinutes: number;
  targetSpecificBatch: boolean;
  selectedBatch?: Batch;
}

/**
 * React component for edit event view
 */
class CreateEvent extends React.Component<Props, State> {

  /**
   * Constructor
   *
   * @param props component props
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      locations: [],
      loading: false,
      redirect: false,
      generatedWoodType: "",
      saving: false,
      messageVisible: false,
      open: false,
      targetSpecificBatch: false,
      treeSpecies: [],
      event: {
        startTime: new Date(),
        data: {},
        type: EventType.Sowing,
        batchId: ""
      },
      performedCultivationActions: [],
      batches: [],
      gritBatches: [],
      peatBatches: [],
      durationHours: 0,
      durationMinutes: 0,
      selectedBatch: undefined
    };
  }

  /**
   * Component did mount life-sycle method
   */
  public componentDidMount = async () => {
    const  { keycloak, facility } = this.props;
    if (!keycloak) {
      return;
    }

    this.setState({loading: true});

    const [locationsService, performedCultivationActionsService, batchesService, gritBatchService, peatBatchService, treeSpeciesService] = await Promise.all([
      Api.getLocationsService(keycloak),
      Api.getPerformedCultivationActionsService(keycloak),
      Api.getBatchesService(keycloak),
      Api.getGritBatchesService(keycloak),
      Api.getPeatBatchesService(keycloak),
      Api.getTreeSpeciesService(keycloak)
    ]);
    const [locations, performedCultivationActions, batches, gritBatches, peatBatches, treeSpecies] = await Promise.all([
      locationsService.listLocations({ facility: facility }),
      performedCultivationActionsService.listCultivationActions({ facility: facility }),
      batchesService.listBatches({ facility: facility }),
      gritBatchService.listGritBatches({ facility: facility }),
      peatBatchService.listPeatBatches({ facility: facility }),
      treeSpeciesService.listTreeSpecies({ facility: facility
  })
    ]);

    this.setState({
      loading: false,
      locations: locations,
      batches: batches,
      gritBatches: gritBatches,
      peatBatches: peatBatches,
      treeSpecies: treeSpecies,
      performedCultivationActions: performedCultivationActions ?? [],
      event: {
        startTime: DateTime.now().toJSDate(),
        data: {},
        type: EventType.Sowing,
        batchId: ""
      }
    });
  }

  public componentDidUpdate = () => {
    const data = (this.state.event ? this.state.event.data : {}) as any;
    if (!data) {
      return;
    }
  }

  /**
   * Render edit product view
   */
  public render() {
    if (this.state.loading) {
      return (
        <Grid style={{paddingTop: "100px"}} centered>
          <Loader inline active size="medium" />
        </Grid>
      );
    }

    if (this.state.redirect) {
      return <Navigate replace={true} to="/events"/>;
    }

    if (!this.state.event) {
      return null;
    }
    const { event } = this.state;

    const eventTypeOptions = Object.values(EventType).map((eventType) => {
      return {
        key: eventType,
        value: eventType,
        text: LocalizedUtils.getLocalizedEventType(eventType as EventType)
      };
    });

    const filteredBatches = this.state.batches.filter(batch => batch.status !== BatchStatus.Split && batch.status !== BatchStatus.Sent);

    const batchesOptions: DropdownItemProps[] = (filteredBatches ?? []).map((batch) => {
      const id = batch.id || "";
      const name = batch ? `${batch.batchNumber} (${batch.unit ? LocalizedUtils.getLocalizedBatchUnit(batch.unit) : ""} = ${batch.amount})` : "";

      return {
        key: id,
        value: id,
        text: name,
        description: LocalizedUtils.getLocalizedBatchStatus(batch.status)
      };
    });

    return (
      <Grid>
        <Grid.Row className="content-page-header-row">
          <Grid.Column width={6}>
            <h2>{strings.addEventHeader}</h2>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={8}>
          <FormContainer>
            <Form.Select required label={strings.labelEventType} name="type" options={eventTypeOptions} value={event.type || ""} onChange={this.handleBaseChange} />
              {event.type !== EventType.Sowing &&
              <Form.Field required>
                <label>{strings.labelBatchNumber}</label>
                <Select name="batchId" options={batchesOptions} onChange={this.handleBaseChange} value={event.batchId} />
              </Form.Field>
              }
              <Form.Field required>
                <label>{strings.labelStartTime}</label>
                <DateTimeInput localization="fi-FI" dateTimeFormat="DD.MM.YYYY HH:mm" onChange={this.handleTimeChange} name="startTime" value={moment(event.startTime).format("DD.MM.YYYY HH:mm")} />
              </Form.Field>
              <Form.Field>
                <label>{strings.labelEndTime}</label>
                <DateTimeInput localization="fi-FI" dateTimeFormat="DD.MM.YYYY HH:mm" onChange={this.handleTimeChange} name="endTime" value={moment(event.endTime).format("DD.MM.YYYY HH:mm")} />
              </Form.Field>
              {this.renderEventDataForm(event)}
              <Form.Group>
                <Form.Input
                  width={6}
                  onChange={(e) => this.setState({durationHours: parseInt(e.target.value)})}
                  name="durationHours"
                  type="number"
                  label={strings.durationHours}
                  value={this.state.durationHours}
                  min={0}
                />
                <Form.Input
                  label={strings.durationMinutes}
                  width={6}
                  name="durationMinutes"
                  type="number"
                  value={this.state.durationMinutes}
                  onChange={(e) => this.setState({durationMinutes: parseInt(e.target.value)})}
                  min={0}
                  max={59}
                  
                />
              </Form.Group>
              <Form.TextArea label={strings.labelAdditionalInformation} onChange={this.handleBaseChange} name="additionalInformation" value={event.additionalInformation} />
              <Message
                success
                visible={this.state.messageVisible}
                header={strings.savedSuccessfully}
              />
              <Button
                className="submit-button"
                onClick={this.handleSubmit}
                type='submit'
                loading={this.state.saving}
              >
                {strings.save}
              </Button>
              <Button onClick={() => this.setState({redirect: true})}>
                {strings.goBack}
              </Button>
            </FormContainer>
          </Grid.Column>
        </Grid.Row>
        <Confirm open={this.state.open} size={"mini"} content={strings.deleteEventConfirmText} onCancel={()=>this.setState({open:false})} onConfirm={this.handleDelete} />
      </Grid>
    );
  }

    /**
   * Handle value change
   *
   * @param event event
   */
  private handleTimeChange = (e: any, { name, value }: DropdownProps | TextAreaProps) => {
    const eventData: any = this.state.event;
    if (!eventData) {
      return;
    }

    eventData[name] = moment(value as any, "DD.MM.YYYY HH:mm").toDate()
    this.setState({ event: eventData });
  }

  /**
   * Handle value change
   *
   * @param event event
   */
  private handleBaseChange = (e: any, data: DropdownProps | TextAreaProps | InputOnChangeData) => {
    const eventData: any = this.state.event;
    if (!eventData) {
      return;
    }

    if ('name' in data && 'value' in data) {
      eventData[data.name] = data.value;
      this.setState({ event: { ...eventData } });
    }
  }

  /**
   * Handle value change
   *
   * @param event event
   */
  private handleDataChange = (e: any, { name, value }: InputOnChangeData | DropdownProps | TextAreaProps) => {
    const eventData = {...this.state.event} as any;
    if (!eventData) {
      return;
    }

    eventData.data = {...this.state.event!.data};
    if (name === "woodType") {
      const generatedWoodType = this.getGeneratedWoodType() + value;
      eventData.data.woodType = generatedWoodType;
    } else {
      eventData.data[name] = value;
    }

    this.setState({ event: { ...eventData } });
  }

  /**
   * Handle form submit
   */
  private handleSubmit = async () => {
    const { event, selectedBatch } = this.state;
    const { keycloak, facility, onError } = this.props;
    try {
      if (!keycloak || !event) {
        return;
      }

      this.setState({saving: true});
      const batchesService = await Api.getBatchesService(keycloak);
      const eventsService = await Api.getEventsService(keycloak);

      if (event.type === EventType.Sowing) {
        const eventData = event.data as SowingEventData;

        if(selectedBatch === undefined) {
          const batch = await batchesService.createBatch({
            batch: {
              batchNumber: "",
              amount: eventData.amount,
              status: BatchStatus.Sowing,
              locationId: eventData.locationId,
            },
            facility: facility
          });
          event.batchId = batch.id!;
        } else {
          event.batchId = selectedBatch.id!;
        }
      }

      event.timeSpentMinutes = this.state.durationHours * 60 + this.state.durationMinutes;

      await eventsService.createEvent({
        event: event as Event,
        facility: facility
      });
      this.setState({saving: false, messageVisible: true});
      setTimeout(() => {
        this.setState({messageVisible: false});
      }, 3000);
    } catch (error: any) {
      Api.handleApiError(error, onError);
    }
  }

  /**
   * Handle product delete
   */
  private handleDelete = async () => {
    const { event } = this.state;
    const { keycloak, facility, onError } = this.props;
    try {
      if (!keycloak || !event) {
        return;
      }

      const eventsService = await Api.getEventsService(keycloak);
      const id = event.id || "";

      await eventsService.deleteEvent({
        eventId: id,
        facility: facility
      });

      this.setState({redirect: true});
    } catch (e: any) {
      Api.handleApiError(e, onError);
    }
  }

  /**
   * Renders form suitable for specific event type
   *
   * @param event event
   */
  private renderEventDataForm = (event: Partial<Event>) => {
    switch (event.type) {
      case "TRANSFER":
        return this.renderTransferDataForm(event.data as TransferEventData);
      case "SOWING":
        return this.renderSowingDataForm(event.data as SowingEventData);
      case "WASTAGE":
        return this.renderWastageDataForm(event.data as WastageEventData);
      case "PERFORMED_ACTION":
        return this.renderPerformedActionDataForm(event.data as PerformedActionEventData);
      case "LOAD":
        return this.renderLoadDataForm(event.data as LoadEventData);
      case "PACKING":
        return this.renderPackingDataForm(event.data as PackingEventData);
      default:
        return null;
    }
  }

  /**
   * Renders trasfer event form
   *
   * @param data transfer event data
   */
  private renderTransferDataForm = (data: TransferEventData) => {
    const sourceLocation = this.state.event?.batchId ? this.state.batches.find(batch => batch.id === this.state.event?.batchId)?.locationId : data.sourceLocationId;
    const locationName = this.state.locations.find(location => location.id === sourceLocation)?.name ?? "-";
    return (
      <React.Fragment>
        <Form.Field label={`${strings.sourceLocation}: ${locationName}`} />
        <Form.Select
         required label={strings.targetLocation} name="targetLocationId" type="targetLocationId" options={this.getLocationOptions()} value={data.targetLocationId} onChange={this.handleDataChange} />
        <Form.Input  required label={strings.amount} name="amount" type="number" value={data.amount} onChange={this.handleDataChange} />
      </React.Fragment>
    );
  }

  /**
   * Renders performed action event form
   *
   * @param data performed action event data
   */
  private renderPerformedActionDataForm = (data: PerformedActionEventData) => {
    const performedActionOptions = (this.state.performedCultivationActions ?? []).map((action) => {
      return {
        key: action.id,
        value: action.id,
        text: LocalizedUtils.getLocalizedValue(action.name)
      };
    });

    return (
      <React.Fragment>
        <Form.Select required label={strings.labelPerformedCultivationActions} options={ performedActionOptions } name="cultivationActionId" value={ data.cultivationActionId} onChange={ this.handleDataChange }/>
        <Form.Input required label={strings.labelAmount} name="amount" type="number" value={data.amount} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelValue} name="value" type="number" value={data.value} onChange={this.handleDataChange} />
        <Form.TextArea required label={strings.labelActionAdditionalInformation} onChange={this.handleDataChange} name="notes" value={data.notes} />
      </React.Fragment>
    );
  }

  /**
   * Renders loading event form
   *
   * @param data loading event data
   */
  private renderLoadDataForm = (data: LoadEventData) => {
    return (
      <React.Fragment>
        <Form.Select label={strings.targetLocation} name="sourceLocationId" options={this.getLocationOptions()} value={data.sourceLocationId} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelDriver} name="driver" type="text" value={data.driver} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelBoxAmount} name="amount" type="number" value={data.amount} onChange={this.handleDataChange} />
      </React.Fragment>
    );
  }

  /**
   * Renders packing event form
   *
   * @param data packing event data
   */
  private renderPackingDataForm = (data: PackingEventData) => {
    const containerTypeOptions = Object.values(PackingContainerType).map((containerType) => {
      return {
        key: containerType,
        value: containerType,
        text: LocalizedUtils.getLocalizedContainerType(containerType)
      };
    });

    const packingTypeOptions = Object.values(PackingType).map((packingType) => {
      return {
        key: packingType,
        value: packingType,
        text: LocalizedUtils.getLocalizedPackingType(packingType)
      };
    });

    const sourceLocation = this.state.event?.batchId ? this.state.batches.find(batch => batch.id === this.state.event?.batchId)?.locationId : data.sourceLocationId;
    const locationName = this.state.locations.find(location => location.id === sourceLocation)?.name ?? "-";

    return (
      <React.Fragment>
        <Form.Field label={`${strings.sourceLocation}: ${locationName}`} />
        <Form.Select label={strings.targetLocation} name="targetLocationId" options={this.getLocationOptions()} value={data.targetLocationId} onChange={this.handleDataChange} />
        <Form.Input required label={strings.largeTrayAmount} name="largeTrayAmount" type="number" value={data.largeTrayAmount} onChange={this.handleDataChange} />
        <Form.Select required label={strings.labelContainerType} name="containerType" options={containerTypeOptions} value={data.containerType} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelBoxAmount} name="boxAmount" type="number" value={data.boxAmount} onChange={this.handleDataChange} />
        <Form.Select required label={strings.labelPackingType} name="packingType" options={packingTypeOptions} value={data.packingType} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelPersonCount} name="personCount" type="number" value={data.personCount} onChange={this.handleDataChange} />
      </React.Fragment>
    );
  }

  /**
   * Renders sowing event form
   *
   * @param data sowing event data
   */
  private renderSowingDataForm = (data: SowingEventData) => {
    if (!this.state.seedBatches) {
      this.loadSowingData().catch((e) => {
        Api.handleApiError(e, this.props.onError);

      });

      return;
    }
    const seedBatchOptions = this.state.seedBatches.map((seedBatch) => {
      return {
        key: seedBatch.id,
        value: seedBatch.id,
        text: `${seedBatch.svCode} (${seedBatch.containerNumber})`
      };
    });
    const gritBatchOptions = this.state.gritBatches.map((batch) => {
      return {
        key: batch.id,
        value: batch.id,
        text: batch.batchNumber
      };
    });
    gritBatchOptions.unshift({ key: "", value: "", text: "" });

    const peatBatchOptions = this.state.peatBatches.map((batch) => {
      return {
        key: batch.id,
        value: batch.id,
        text: batch.batchNumber
      };
    });

    const filteredBatches = this.state.batches.filter(batch => batch.status !== BatchStatus.Split && batch.status !== BatchStatus.Sent);

    const batchesOptions: DropdownItemProps[] = (filteredBatches ?? []).map((batch) => {
      const id = batch.id ?? "";
      const name = batch ? `${batch.batchNumber} (${batch.unit ? LocalizedUtils.getLocalizedBatchUnit(batch.unit) : ""} = ${batch.amount})` : "";
      return {
        key: id,
        value: id,
        text: name,
        description: LocalizedUtils.getLocalizedBatchStatus(batch.status)
      };
    });

    return (
      <React.Fragment>
        <Form.Input required label={strings.cellAmount} name="amount" type="number" value={data.amount} onChange={this.handleDataChange} />
        <Form.Select required label={strings.labelSeedBatch} name="seedBatchId" options={seedBatchOptions} value={data.seedBatchId} onChange={this.handleDataChange} />
        <Form.Input required label={strings.seedAmount} name="seedAmount" type="number" value={data.seedAmount} onChange={this.handleDataChange} />
        <Form.Group>
          <Form.Input
            width={4}
            readOnly
            label={strings.timber}
            value={this.getGeneratedWoodType()}
          />
          <Form.Input
            label={strings.woodTypeFill}
            width={12}
            required
            name="woodType"
            type="text"
            // Get text from woodtype after second slash
            value={data.woodType ? data.woodType.split("/")[2] : ""}
            onChange={this.handleDataChange}
          />
        </Form.Group>
        <Form.Select required label={strings.location} name="locationId" options={this.getLocationOptions()} value={data.locationId} onChange={this.handleDataChange} />
        <Form.Group>
          <Form.Field>
            <label>{strings.labelTargetToBatch}</label>
            <Checkbox
              toggle
              name="targetSpecificBatch"
              checked={this.state.targetSpecificBatch}
              onChange={() => {
                !this.state.targetSpecificBatch === false && this.setState({ selectedBatch: undefined })
                this.setState((prevState) => ({ targetSpecificBatch: !prevState.targetSpecificBatch }))
              }
              }
            />
          </Form.Field>
          {this.state.targetSpecificBatch && (
            <Form.Select
              label={strings.labelBatchNumber}
              name="batchId"
              options={batchesOptions}
              value={this.state.selectedBatch?.id}
              onChange={(e: any, { value }: DropdownProps ) => {
                const selectedBatch = this.state.batches.find((batch) => batch.id === value);
                this.setState({ selectedBatch: selectedBatch });
              }
              }
            />
        )}
        </Form.Group>
        <Form.Select label={strings.gritBatches} name="gritBatchId" options={gritBatchOptions} value={data.gritBatchId} onChange={this.handleDataChange} />
        <Form.Input label={strings.amount} name="gritAmount" type="number" value={data.gritAmount} onChange={this.handleDataChange} />
        <Form.Select required label={strings.peatBatches} name="peatBatchId" options={peatBatchOptions} value={data.peatBatchId} onChange={this.handleDataChange} />
        <Form.Input required label={strings.amount} name="peatAmount" type="number" value={data.peatAmount} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelPersonCount} name="amountOfPersons" type="number" value={data.amountOfPersons} onChange={this.handleDataChange} />
      </React.Fragment>
    );
  }

  /**
   * Generate wood type
   */
  private getGeneratedWoodType = () => {
    const eventData = this.state.event.data as SowingEventData;
    if (!eventData) {
      return "";
    }
    const treeSpeciesId = this.state.seedBatches?.find((seedBatch) => seedBatch.id === eventData.seedBatchId)?.treeSpeciesId ?? "";

    if (!treeSpeciesId) {
      return "";
    }

    const treeSpecies = this.state.treeSpecies?.find((treeSpecies) => treeSpecies.id === treeSpeciesId);
    const selectedYear = this.state.event.startTime ? DateTime.fromJSDate(this.state.event.startTime).year : DateTime.now().year;

    const woodType = `${treeSpecies?.code}/-${selectedYear.toString().slice(-2)}/`;
    return woodType;
  }


  /**
   * Renders wastage event form
   *
   * @param data wastage event data
   */
  private renderWastageDataForm = (data: WastageEventData) => {
    if (!this.state.wastageReasons) {
      this.loadWastageData().catch((e) => {
        Api.handleApiError(e, this.props.onError);
      });

      return;
    }

    const wastageReasonOptions = this.state.wastageReasons.map((wastageReason) => {
      return {
        key: wastageReason.id,
        value: wastageReason.id,
        text: LocalizedUtils.getLocalizedValue(wastageReason.reason)
      };
    });

    const phaseOptions = Object.values(EventType).map((phase) => {
      return {
        key: phase,
        value: phase,
        text: LocalizedUtils.getLocalizedEventType(phase as EventType)
      };
    })

    const mapBatchStatusToPhase = (status: BatchStatus | undefined) => {
    switch (status) {
      case BatchStatus.Growth:
      case BatchStatus.Sowing:
        return EventType.Sowing;
      default:
        return EventType.Packing;
      }
    }

    const batchStatus = this.state.event?.batchId ? this.state.batches.find(batch => batch.id === this.state.event?.batchId)?.status : undefined;

    this.state.event.data = {
      ...data,
      phase: mapBatchStatusToPhase(batchStatus)
    };

    return (
      <React.Fragment>
        <Form.Select required label={strings.labelWastageReason} name="reasonId" options={wastageReasonOptions} value={data.reasonId} onChange={this.handleDataChange} />
        { batchStatus && <Form.Select required disabled label={strings.labelPhase} name="phase" options={phaseOptions} value={mapBatchStatusToPhase(batchStatus)} onChange={this.handleDataChange} />}
        <Form.Select label={strings.labelLocation} name="locationId" options={this.getLocationOptions()} value={data.locationId} onChange={this.handleDataChange} />
        <Form.Input required label={strings.labelAmount} name="amount" type="number" value={data.amount} onChange={this.handleDataChange} />
      </React.Fragment>
    );
  }

  /**
   * Get location options
   *
   * @returns location options
   */
  private getLocationOptions = () => {
    const locationOptions = this.state.locations.map((location) => {
      return {
        key: location.id ?? "",
        value: location.id ?? "",
        text: location.name ?? ""
      };
    });

    return [{key: "", value: "", text: ""}].concat(locationOptions);
  }

  /**
   * Loads data required for sowing event
   */
  private loadSowingData = async () => {
    const { keycloak, facility } = this.props;
    if (!keycloak) {
      return;
    }

    this.setState({loading: true});
    const [seedBatchesService] = await Promise.all([
      Api.getSeedBatchesService(keycloak),
      Api.getLocationsService(keycloak)
    ]);

    const [seedBatches] = await Promise.all([
      seedBatchesService.listSeedBatches({ facility: facility }),
    ]);

    this.setState({
      loading: false,
      seedBatches: seedBatches,
    });

  }

  /**
   * Loads data required for wastage event
   */
  private loadWastageData = async () => {
    const { keycloak, facility } = this.props;
    if (!keycloak) {
      return;
    }

    this.setState({loading: true});
    const [wastageReasonsService] = await Promise.all([
      Api.getWastageReasonsService(keycloak),
      Api.getLocationsService(keycloak)
    ]);

    const [wastageReasons] = await Promise.all([
      wastageReasonsService.listWastageReasons({ facility: facility }),
    ]);

    this.setState({
      loading: false,
      wastageReasons: wastageReasons,
    });

  }
}

/**
 * Redux mapper for mapping store state to component props
 *
 * @param state store state
 */
export function mapStateToProps(state: StoreState) {
  return {
    facility: state.facility
  };
}

/**
 * Redux mapper for mapping component dispatches
 *
 * @param dispatch dispatch method
 */
export function mapDispatchToProps(dispatch: Dispatch<actions.AppAction>) {
  return {
     onError: (error: ErrorMessage | undefined) => dispatch(actions.onErrorOccurred(error))
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateEvent);